import React, { useState, useEffect } from 'react';
import { StageProps } from '../types';

const FirstRaceReview: React.FC<StageProps> = ({ appData, setAppData, onNext, onPrev }) => {
  const [raceName, setRaceName] = useState('');

  useEffect(() => {
    // Generate a smart default race name from the filename
    if (appData.newRaceData?.raceName) {
      setRaceName(appData.newRaceData.raceName);
    } else if (appData.newRaceData?.fileName) {
      // Clean up filename to make a good race name (don't auto-truncate)
      const cleanName = appData.newRaceData.fileName
        .replace(/\.[^/.]+$/, '') // Remove file extension
        .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
        .replace(/\b\w/g, l => l.toUpperCase()); // Title case
      setRaceName(cleanName);
    } else {
      setRaceName('Race 1');
    }
  }, [appData.newRaceData]);

  const handleNext = () => {
    if (raceName.trim()) {
      setAppData({
        ...appData,
        newRaceData: {
          ...appData.newRaceData!,
          raceName: raceName.trim()
        }
      });
      onNext?.();
    }
  };

  if (!appData.newRaceData) {
    return (
      <div className="stage-container">
        <div className="error">
          <strong>Error:</strong> No race data found. Please go back and upload a race file.
        </div>
        <div className="stage-actions">
          <button className="btn btn-secondary" onClick={onPrev}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const { participants, headers } = appData.newRaceData;
  const categories = Array.from(new Set(participants.map(p => p.category).filter(Boolean)));
  
  // Sample participant for field preview
  const sampleParticipant = participants[0];

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>📋 Review Race Data Structure</h2>
        <p>Review the categories and data fields that will be used for your race series.</p>
      </div>

      <div className="review-section">
        <h3>🏁 Race Configuration</h3>
        <div className="form-group">
          <label htmlFor="raceName">
            Race Name <span style={{ color: '#718096' }}>(will appear as column header in series results)</span>
          </label>
          <input
            id="raceName"
            type="text"
            className={`form-control ${raceName.length > 15 ? 'warning-border' : ''}`}
            value={raceName}
            onChange={(e) => setRaceName(e.target.value)}
            placeholder="Enter a race name..."
            style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              border: raceName.length > 15 ? '2px solid #f56565' : '2px solid #e2e8f0',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
          />
          {raceName.length > 15 ? (
            <div style={{ 
              color: '#c53030', 
              marginTop: '0.5rem', 
              display: 'block',
              fontWeight: '600',
              backgroundColor: '#fed7d7',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #feb2b2'
            }}>
              ⚠️ <strong>Name too long!</strong> Consider shortening by {raceName.length - 15} characters for better column headers.
            </div>
          ) : (
            <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
              💡 Keep it short for better column headers (e.g., "Race 1", "Spring XC", "Nationals")
            </small>
          )}
        </div>
      </div>

      <div className="review-section">
        <h3>📊 Race Overview</h3>
        <div className="overview-stats">
          <div className="stat-item">
            <div className="stat-number">{participants.length}</div>
            <div className="stat-label">Total Participants</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{categories.length}</div>
            <div className="stat-label">Categories</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{headers.length}</div>
            <div className="stat-label">Data Fields</div>
          </div>
        </div>
      </div>

      <div className="review-section">
        <h3>🏷️ Categories Found</h3>
        <div className="category-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category Name</th>
                <th>Participants</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category, index) => {
                const categoryCount = participants.filter(p => p.category === category).length;
                return (
                  <tr key={index}>
                    <td><strong>{category}</strong></td>
                    <td>{categoryCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="review-section">
        <h3>📝 Data Fields</h3>
        <div className="fields-grid">
          {headers.map((header, index) => {
            const getFieldType = (fieldName: string) => {
              const lower = fieldName.toLowerCase();
              
              if (lower.includes('position') || lower.includes('rank') || lower.includes('place')) {
                return 'Position';
              }
              if (lower.includes('time') || lower.includes('duration')) {
                return 'Time';
              }
              if (lower.includes('name') && !lower.includes('first') && !lower.includes('last')) {
                return 'Name';
              }
              if (lower.includes('firstname') || lower.includes('first_name')) {
                return 'First Name';
              }
              if (lower.includes('lastname') || lower.includes('last_name')) {
                return 'Last Name';
              }
              if (lower.includes('category') || lower.includes('class') || lower.includes('division')) {
                return 'Category';
              }
              if (lower.includes('license') || lower.includes('licence') || lower.includes('id') || lower.includes('number')) {
                return 'Identifier';
              }
              if (lower.includes('team') || lower.includes('sponsor')) {
                return 'Team/Sponsor';
              }
              if (lower.includes('city') || lower.includes('town')) {
                return 'City';
              }
              if (lower.includes('state') || lower.includes('province') || lower.includes('region')) {
                return 'State/Province';
              }
              if (lower.includes('points') || lower.includes('score')) {
                return 'Points';
              }
              
              return 'General';
            };
            
            const fieldType = getFieldType(header);
            const isIdentifier = fieldType === 'Identifier';
            
            return (
              <div key={index} className={`field-item ${isIdentifier ? 'identifier-field' : ''}`}>
                <div className="field-name">
                  {header}
                  {isIdentifier && <span className="identifier-badge">🔑</span>}
                </div>
                <div className="field-type">{fieldType}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="review-section">
        <h3>👤 Sample Participant Data</h3>
        <div className="sample-data">
          {Object.entries(sampleParticipant)
            .filter(([key, value]) => key !== 'id' && value !== undefined && value !== null && value !== '')
            .map(([key, value]) => (
              <div key={key} className="sample-row">
                <span className="sample-label">{key}:</span>
                <span className="sample-value">{String(value)}</span>
              </div>
            ))}
        </div>
        <small style={{ color: '#718096', marginTop: '1rem', display: 'block' }}>
          💡 This shows actual data from the first participant in your CSV
        </small>
      </div>

      <div className="info-box">
        <h4>✅ What happens next?</h4>
        <p>
          This data structure will be used as the foundation for your race series. 
          The race name "<strong>{raceName || 'Your Race Name'}</strong>" will appear as a column header in the series standings.
        </p>
        <ul>
          <li>Categories: <strong>{categories.join(', ')}</strong></li>
          <li>Required fields: Name, Category, Position</li>
          <li>Optional fields: Time, License, Team, etc.</li>
        </ul>
      </div>

      <div className="stage-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          ← Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!raceName.trim()}
        >
          Next: Configure Points →
        </button>
      </div>
    </div>
  );
};

export default FirstRaceReview; 